import { useEffect } from 'react';
import {
  mapToAccountsModels,
  mapToCustomerNumberModel,
  updateConsumerAccounts,
  updateConsumerAddCustomerNumbers,
  updateConsumerModelAccountsStatus,
  updateConsumerModelIsInclusive,
} from '../../mappers/ConsumerAccountMapper';
import { mapToTripAccessModel, updateConsumerModelTripAccess } from '../../mappers/ConsumerMapper';
import { mapToConsumerModel } from '../../mappers/ConsumerToDTOMapper';
import { ApiError } from '../../models/Api/ApiError';
import { AccountModel } from '../../models/Consumer/Account/AccountModel';
import { AccountsFormSaveModel } from '../../models/Consumer/Account/AccountsFormSaveModel';
import { CustomerNumbersFormViewModel } from '../../models/Consumer/Account/CustomerNumbersFormViewModel';
import { ConsumerFormViewModel } from '../../models/Consumer/ConsumerFormViewModel';
import { ConsumerResponseModel } from '../../models/Consumer/ConsumerResponseModel';
import { ConsumerRequestModel } from '../../models/Consumer/ConsumerRequestModel';
import { ScopesPresetModel } from '../../models/Consumer/Scopes/ScopesPresetModel';
import { SmIdModel } from '../../models/Consumer/SmIdModel';
import { TripAccessFormViewModel } from '../../models/Consumer/TripAccessFormViewModel';
import { clearConsumerInfoErrorAction, fetchConsumerInfoAction } from '../actions/consumerInfoActions';
import { fetchConsumersAction, updateConsumerAction } from '../actions/consumersActions';
import { resetConsumer } from '../reducers/consumerInfoReducer';
import { resetConsumerTypes } from '../reducers/consumerTypesReducer';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
/* eslint-disable @typescript-eslint/no-floating-promises */

export const useConsumerInfo: (id: string) => [
  {
    consumer: ConsumerResponseModel;
    isLoading: boolean;
    error?: ApiError;
  },
  {
    updateConsumer: (
      model: ConsumerFormViewModel,
      scopesPresets: ScopesPresetModel[],
      prevModel?: ConsumerResponseModel,
    ) => void;
    updateConsumerAccount: (
      consumer: ConsumerResponseModel,
      accountsForm: AccountsFormSaveModel,
      prevModel?: AccountModel,
    ) => void;
    updateConsumerCustomerNumbers: (
      model: ConsumerResponseModel,
      customerNumber: CustomerNumbersFormViewModel,
      accountId: string,
      prevModel?: SmIdModel,
    ) => void;
    updateConsumerTripAccess: (
      model: ConsumerResponseModel,
      tripAccess?: TripAccessFormViewModel,
      scopesPreset?: ScopesPresetModel[],
    ) => void;
    updateConsumerIsInclusive: (model: ConsumerResponseModel, isInclusive: boolean) => void;
    updateConsumerAccountsStatus: (
      model: ConsumerResponseModel,
      accountIds: string[],
      enabled: boolean,
    ) => void;
    handleError: () => void;
  },
] = (id) => {
  const dispatch = useAppDispatch();
  const consumerInfos = useAppSelector((s) => s.consumerInfo);

  useEffect(() => {
    if (consumerInfos.items[id] === undefined) {
      dispatch(fetchConsumerInfoAction(id));
    }
  }, [consumerInfos.items, dispatch, id]);

  useEffect(() => {
    Promise.all([dispatch(resetConsumerTypes()), dispatch(resetConsumer())]);
  }, [dispatch]);

  const updateConsumer = (
    model: ConsumerFormViewModel,
    scopesPresets: ScopesPresetModel[],
    prevModel?: ConsumerResponseModel,
  ) => {
    const mappedModel = mapToConsumerModel(model, scopesPresets, prevModel);
    dispatch(updateConsumerAction(mappedModel))
      .unwrap()
      .then(() => {
        dispatch(fetchConsumersAction());
        dispatch(fetchConsumerInfoAction(id));
      });
  };

  const updateConsumerAccount = (
    consumer: ConsumerResponseModel,
    accountsForm: AccountsFormSaveModel,
    prevModel?: AccountModel,
  ) => {
    const mappedAccounts = mapToAccountsModels(accountsForm, prevModel);
    const mappedModel = updateConsumerAccounts(consumer, mappedAccounts, accountsForm.accountsIds);

    dispatch(updateConsumerAction(mappedModel))
      .unwrap()
      .then(() => {
        dispatch(fetchConsumersAction());
        dispatch(fetchConsumerInfoAction(id));
      });
  };

  const updateConsumerCustomerNumbers = (
    model: ConsumerResponseModel,
    customerNumber: CustomerNumbersFormViewModel,
    accountId: string,
    prevModel?: SmIdModel,
  ) => {
    const mappedCustomerNumberModel = mapToCustomerNumberModel(customerNumber);
    const mappedModel: ConsumerRequestModel = updateConsumerAddCustomerNumbers(
      model,
      mappedCustomerNumberModel,
      accountId,
      prevModel?.id,
    );
    dispatch(updateConsumerAction(mappedModel))
      .unwrap()
      .then(() => {
        dispatch(fetchConsumersAction());
        dispatch(fetchConsumerInfoAction(id));
      });
  };

  const updateConsumerTripAccess = (
    model: ConsumerResponseModel,
    tripAccess?: TripAccessFormViewModel,
    scopesPreset?: ScopesPresetModel[],
  ) => {
    const mappedTripAccess = mapToTripAccessModel(tripAccess, scopesPreset);
    const mappedModel: ConsumerRequestModel = updateConsumerModelTripAccess(model, mappedTripAccess);
    dispatch(updateConsumerAction(mappedModel))
      .unwrap()
      .then(() => {
        dispatch(fetchConsumersAction());
        dispatch(fetchConsumerInfoAction(id));
      });
  };

  const updateConsumerIsInclusive = (model: ConsumerResponseModel, isInclusive: boolean) => {
    const mappedModel = updateConsumerModelIsInclusive(model, isInclusive);
    dispatch(updateConsumerAction(mappedModel))
      .unwrap()
      .then(() => {
        dispatch(fetchConsumersAction());
        dispatch(fetchConsumerInfoAction(id));
      });
  };

  const updateConsumerAccountsStatus = (
    model: ConsumerResponseModel,
    accountIds: string[],
    enabled: boolean,
  ) => {
    const mappedModel = updateConsumerModelAccountsStatus(model, accountIds, enabled);
    dispatch(updateConsumerAction(mappedModel))
      .unwrap()
      .then(() => {
        dispatch(fetchConsumersAction());
        dispatch(fetchConsumerInfoAction(id));
      });
  };

  const handleError = () => {
    dispatch(clearConsumerInfoErrorAction());
  };

  return [
    { consumer: consumerInfos.items[id], isLoading: consumerInfos.isLoading, error: consumerInfos.error },
    {
      updateConsumer,
      updateConsumerAccount,
      updateConsumerCustomerNumbers,
      updateConsumerTripAccess,
      updateConsumerIsInclusive,
      updateConsumerAccountsStatus,
      handleError,
    },
  ];
};
